import {noteClassification} from './noteClassification';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";

import { db } from "../Security/firebase-config";
import { createUnplannedMatchEvent, normalizeFocusPoint } from "./teamPlanning";
import {eventSlots, flattenSetupEvent, hasSetupMap, matchTitle as sharedMatchTitle, parseSetupKey, projectionStamp, replaceSetupEvent, resolveSetupEvent} from "./eventSetup";
import {eventOwnerRef} from "./eventSetupStore";
import {lineupProjection} from "./matchLineup";
import {loadEventPrepFocusPoints} from "./eventPrepFocusPoints";

async function linkSetupRecording({teamId, mapId, mapName, matchId, playedAt, actor, calendarEventId}) {
  const identity = parseSetupKey(calendarEventId);
  if (identity.scope !== "team") throw new Error("Personal events can only be linked to local desktop recordings.");
  const ref = eventOwnerRef(teamId);
  return runTransaction(db, async transaction => {
    const team = await transaction.get(ref);
    const events = team.data()?.events || [];
    const event = resolveSetupEvent(events, calendarEventId);
    if (!event) throw new Error("The selected event was deleted. Choose another event or create an unplanned event.");
    const slots = eventSlots(event);
    const slot = slots.find(item => item.id === identity.slotId);
    if (!slot) throw new Error("This map slot no longer exists.");
    if (!hasSetupMap(slot)) throw new Error("Choose and save the map in Setup before uploading or recording.");
    if (slot.matchId && slot.matchId !== matchId) throw new Error("Another recording was added to this map. Select its existing match to add your POV.");
    if (slot.mapId && slot.mapId !== mapId) throw new Error("The selected map differs from the saved setup. Select the correct map before recording.");
    const matchRef = doc(db, "teams", teamId, "maps", mapId, "matches", matchId);
    const match = await transaction.get(matchRef);
    const existing = match.data() || {};
    if (existing.calendarEventId && existing.calendarEventId !== calendarEventId && existing.calendarEventId !== event.eventId && resolveSetupEvent(events, existing.calendarEventId)) throw new Error("This match belongs to a different event or map slot.");
    if (!slot.statsId && existing.teamStatsMatchId) slot.statsId = existing.teamStatsMatchId;
    const statsRef = slot.statsId ? doc(db, "teams", teamId, "matches", slot.statsId) : null;
    const stats = statsRef ? await transaction.get(statsRef) : null;
    if (stats?.data()?.calendarEventId && stats.data().calendarEventId !== calendarEventId && stats.data().calendarEventId !== event.eventId && resolveSetupEvent(events, stats.data().calendarEventId)) throw new Error("This recording's stats belong to another event map.");
    Object.assign(slot, {mapId, mapName: slot.mapName || mapName || "", matchId});
    transaction.set(matchRef, {
      ...lineupProjection(slot),
      ...(match.exists() ? {} : {createdAt: serverTimestamp(), createdBy: actor?.username || "Member", createdByUid: actor?.uid,
        matchKey: sharedMatchTitle(event, slot).toLowerCase()}),
      title: sharedMatchTitle(event, slot), eventSetupRevision: projectionStamp(event, slot), calendarEventId, linkedCalendarAt: new Date().toISOString(),
      sessionPlanId: event.sessionPlanId || (event.instanceDate ? `${event.eventId}__${event.instanceDate}` : event.eventId), mapId, mapName: slot.mapName,
      matchPlayedAtIso: existing.matchPlayedAtIso || toIso(playedAt), calendarEventTag: event.tag,
      calendarEventStartUTC: event.startUTC, participants: event.participants || [], participantUids: event.participantsUid || [],
      ...(stats?.exists() ? {teamStatsMatchId: slot.statsId, linkedTeamStats: [{teamStatsMatchId: slot.statsId, map: slot.mapName, dateISO: event.startUTC.slice(0, 10)}]} : {}),
    }, {merge: true});
    if (stats?.exists()) transaction.update(statsRef, {...lineupProjection(slot), calendarEventId, eventSetupRevision: projectionStamp(event, slot), title: sharedMatchTitle(event, slot),
      opponent: event.opponentName || "", competition: event.competition || "", map: slot.mapName || "", calendarEventStartUTC: event.startUTC,
      linkedReviewMatches: [{mapId, reviewMatchId: matchId, title: sharedMatchTitle(event, slot)}]});
    const updated = {...event, mapSlots: slots, sessionPlanId: event.instanceDate ? `${event.eventId}__${event.instanceDate}` : event.sessionPlanId || event.eventId};
    transaction.update(ref, {events: replaceSetupEvent(events, event, updated)});
    return {event: flattenSetupEvent(updated, slot), createdUnplannedEvent: false, mapChanged: false, sessionPlan: event.sessionPlan || {}};
  });
}

function makeId(prefix) {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function toIso(value) {
  if (!value) return new Date().toISOString();
  if (typeof value?.toDate === "function") return value.toDate().toISOString();
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : new Date().toISOString();
}

function sanitizeEvent(event) {
  return Object.fromEntries(Object.entries(event).filter(([, value]) => value !== undefined));
}

function normalizeMapName(value) {
  return String(value || "").trim() || null;
}

function mapHasChanged(event, mapId, mapName) {
  const nextMapName = normalizeMapName(mapName);
  const changedId = Boolean(event?.mapId && mapId && event.mapId !== mapId);
  const changedName = Boolean(
    event?.mapName
      && nextMapName
      && String(event.mapName).toLowerCase() !== nextMapName.toLowerCase(),
  );
  return changedId || changedName;
}

export function toLocalDateTimeInputs(value) {
  const date = new Date(toIso(value));
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  const iso = offsetDate.toISOString();
  return { date: iso.slice(0, 10), time: iso.slice(11, 16) };
}

export async function linkMatchToCalendarEvent({
  teamId,
  mapId,
  mapName,
  matchId,
  matchTitle,
  playedAt,
  actor,
  calendarEventId = null,
  allowMapChange = false,
}) {
  if (parseSetupKey(calendarEventId || "")) return linkSetupRecording({teamId, mapId, mapName, matchId, playedAt, actor, calendarEventId});
  if (!teamId || !mapId || !matchId) {
    throw new Error("Missing team, map, or match information for the calendar link.");
  }

  const teamRef = doc(db, "teams", teamId);
  const matchRef = doc(db, "teams", teamId, "maps", mapId, "matches", matchId);
  const fallbackEventId = makeId("unplanned-match");
  const matchingMapName = normalizeMapName(mapName);
  const now = new Date().toISOString();
  const playedAtIso = toIso(playedAt);

  // A map correction after a VOD exists must be audited separately. This guard
  // prevents the normal recording flow from silently moving a reviewed session.
  if (calendarEventId) {
    const currentTeam = await getDoc(teamRef);
    const currentEvent = Array.isArray(currentTeam.data()?.events)
      ? currentTeam.data().events.find((event) => event?.eventId === calendarEventId)
      : null;
    if (currentEvent?.matchId === matchId && mapHasChanged(currentEvent, mapId, matchingMapName) && currentEvent.mapId) {
      const oldVideos = await getDocs(query(
        collection(
          db,
          "teams",
          teamId,
          "maps",
          currentEvent.mapId,
          "videos",
        ),
        where("matchId", "==", matchId),
      ));
      if (oldVideos.size) {
        throw new Error("This linked event already has VODs. Use the audited map-correction flow instead of changing its map here.");
      }
    }
  }

  const result = await runTransaction(db, async (transaction) => {
    const [teamSnapshot, matchSnapshot] = await Promise.all([
      transaction.get(teamRef),
      transaction.get(matchRef),
    ]);
    const teamData = teamSnapshot.exists() ? teamSnapshot.data() || {} : {};
    const events = Array.isArray(teamData.events) ? teamData.events : [];
    const existingMatch = matchSnapshot.exists() ? matchSnapshot.data() || {} : {};
    const existingMatchEventId = String(existingMatch.calendarEventId || "").trim();
    const eventId = calendarEventId || existingMatchEventId || fallbackEventId;

    if (existingMatchEventId && calendarEventId && existingMatchEventId !== calendarEventId) {
      throw new Error("This match is already linked to a different calendar event.");
    }

    let linkedEvent = events.find((event) => event?.eventId === eventId) || null;
    const originalEventMapId = linkedEvent?.mapId || null;
    if (calendarEventId && !linkedEvent) {
      throw new Error("The selected calendar event no longer exists. Refresh and choose it again.");
    }

    if (linkedEvent?.matchId && linkedEvent.matchId !== matchId) {
      throw new Error("The selected calendar event is already linked to another match.");
    }

    const mapChanged = mapHasChanged(linkedEvent, mapId, matchingMapName);
    if (mapChanged && !allowMapChange) {
      throw new Error("The selected event uses a different map and needs confirmation before recording.");
    }

    if (!linkedEvent) {
      linkedEvent = createUnplannedMatchEvent({
        eventId,
        matchId,
        mapId,
        mapName: matchingMapName,
        playedAt: playedAtIso,
        user: actor,
      });
    } else {
      linkedEvent = {
        ...linkedEvent,
        matchId,
        sessionPlanId: linkedEvent.sessionPlanId || eventId,
        mapId: mapId || linkedEvent.mapId || null,
        mapName: matchingMapName || linkedEvent.mapName || null,
        lastEditedAt: Date.now(),
        lastEditedByUid: actor?.uid || linkedEvent.lastEditedByUid || null,
        lastEditedBy: actor?.username || linkedEvent.lastEditedBy || "Member",
      };
    }

    // Session plans are mirrored on the calendar event so match setup can still
    // proceed for teams whose Firestore rules do not yet expose sessionPlans.
    const sessionPlan = linkedEvent.sessionPlan || {};

    const originalMapRef = mapChanged
      && linkedEvent.matchId === matchId
      && originalEventMapId
      && originalEventMapId !== mapId
      ? doc(db, "teams", teamId, "maps", originalEventMapId, "matches", matchId)
      : null;
    const originalMapMatch = originalMapRef ? await transaction.get(originalMapRef) : null;

    const nextEvents = events.some((event) => event?.eventId === eventId)
      ? events.map((event) => event?.eventId === eventId ? sanitizeEvent(linkedEvent) : event)
      : [...events, sanitizeEvent(linkedEvent)];

    transaction.update(teamRef, { events: nextEvents });
    transaction.set(matchRef, {
      ...(matchSnapshot.exists() ? {} : {
        matchKey: String(matchTitle || "Match").toLowerCase().replace(/\s+/g, " ").trim(),
        createdAt: serverTimestamp(),
        createdBy: actor?.username || "Member",
        createdByUid: actor?.uid || null,
      }),
      calendarEventId: eventId,
      sessionPlanId: linkedEvent.sessionPlanId || null,
      linkedCalendarAt: now,
      mapId,
      mapName: matchingMapName,
      title: matchTitle || existingMatch.title || "Match",
      matchPlayedAtIso: playedAtIso,
      calendarEventTag: linkedEvent.tag || null,
      calendarEventStartUTC: linkedEvent.startUTC || playedAtIso,
      participants: Array.isArray(linkedEvent.participants) ? linkedEvent.participants : [],
      participantUids: Array.isArray(linkedEvent.participantsUid) ? linkedEvent.participantsUid : [],
      sessionObjective: String(sessionPlan.objective || "").trim(),
      sessionNotes: String(sessionPlan.notes || "").trim(),
      sessionFocusPointCount: Array.isArray(sessionPlan.focusPoints) ? sessionPlan.focusPoints.length : 0,
    }, { merge: true });

    if (originalMapRef && originalMapMatch?.exists() && originalMapMatch.data()?.calendarEventId === eventId) {
      transaction.delete(originalMapRef);
    }

    return {
      event: linkedEvent,
      createdUnplannedEvent: !calendarEventId && !existingMatchEventId,
      mapChanged,
      sessionPlan,
    };
  });

  return result;
}

export async function copySessionPlanFocusPointsToMatch({ teamId, mapId, matchId, eventId, focusPoints }) {
  if (!teamId || !mapId || !matchId || !eventId) return [];
  const prepared = Array.isArray(focusPoints) ? focusPoints : await loadEventPrepFocusPoints({teamId, eventId});
  const points = prepared.map(normalizeFocusPoint).filter(point => point.text);
  if (!points.length) return [];

  const focusPointsRef = collection(db, "teams", teamId, "maps", mapId, "matches", matchId, "focusPoints");
  const matchRef = doc(db, "teams", teamId, "maps", mapId, "matches", matchId);
  await runTransaction(db, async transaction => {
    // Query after reading the parent; the heartbeat write makes concurrent
    // initializers conflict and re-query instead of creating duplicate points.
    const match = await transaction.get(matchRef);
    if (!match.exists()) throw new Error("That match is no longer available. Select the event again.");
    const existing = await getDocs(focusPointsRef);
    if (existing.size) return;
    transaction.update(matchRef, {reservationLastHeartbeatAt: serverTimestamp()});
    points.forEach((point, index) => {
      transaction.set(doc(focusPointsRef), {
        order: index, text: point.text, ...noteClassification(point), ratings: {},
        outcome: point.outcome || "not_attempted", outcomeNote: point.outcomeNote || "",
        evidence: point.evidence || [], sourceType: point.sourceType || "session_plan",
        sourceId: point.sourceId || null, sourceScope: point.sourceScope || null,
        sourceMapId: point.sourceMapId || null, sessionPlanEventId: eventId,
        requiresReview: point.requiresReview === true, createdAt: point.createdAt || new Date().toISOString(),
      });
    });
  });
  return points;
}
